/* @jsx h */
import { defineComponent, h, ref, watch } from "vue";
import { useAuth } from "@app/vue/providers/Auth";
import {
  DEFAULT_RECOMMENDED_TIP_SETTING,
  type RecommendedTipSetting,
} from "@lemmonhq/types";

export const TipsSettingsCard = defineComponent<{
  recommendedTips: RecommendedTipSetting | null;
  onUpdate: (value: RecommendedTipSetting | null) => void;
}>({
  name: "TipsSettingsCard",
  props: ["recommendedTips", "onUpdate"],
  setup(props) {
    const auth = useAuth();
    const stepsInput = ref(props.recommendedTips?.steps.join(", ") ?? "");

    const parseSteps = (raw: string): number[] => {
      return raw
        .split(",")
        .map((s) => parseFloat(s.trim()))
        .filter((n) => !Number.isNaN(n) && n >= 0);
    };

    const handleEnable = () => {
      props.onUpdate(DEFAULT_RECOMMENDED_TIP_SETTING);
    };

    const handleDisable = () => {
      props.onUpdate(null);
    };

    const handleTypeChange = (type: "percentage" | "absolute") => {
      if (!props.recommendedTips) return;
      props.onUpdate({ ...props.recommendedTips, type });
    };

    const handleStepsBlur = () => {
      if (!props.recommendedTips) return;
      const steps = parseSteps(stepsInput.value);
      if (steps.length > 0) {
        props.onUpdate({ ...props.recommendedTips, steps });
      }
    };

    return () => (
      <div class="l-box-shadow rounded rounded-2xl shadow-xl bg-base-100 border border-base-100">
        <div class="card-body">
          <div class="flex items-center gap-3 mb-6">
            <div class="p-3 bg-success/10 rounded-xl">
              <span class="material-symbols-outlined text-success text-2xl">
                payments
              </span>
            </div>
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-accent">Tip Suggestions</h3>
              <p class="text-sm text-base-content">
                Configure the tip options shown to customers during checkout.
              </p>
            </div>
            <input
              type="checkbox"
              class="toggle toggle-primary"
              checked={props.recommendedTips != null}
              onChange={() =>
                props.recommendedTips != null ? handleDisable() : handleEnable()
              }
            />
          </div>

          {props.recommendedTips != null && (
            <div class="space-y-4">
              <div>
                <label class="label">
                  <span class="label-text text-sm font-medium text-base-content/80">
                    Tip type
                  </span>
                </label>
                <div class="flex gap-3">
                  {(["percentage", "absolute"] as const).map((type) => (
                    <label
                      key={type}
                      class="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        class="radio radio-primary radio-sm"
                        checked={props.recommendedTips!.type === type}
                        onChange={() => handleTypeChange(type)}
                      />
                      <span class="label-text capitalize">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label class="form-control">
                  <span class="label-text text-sm font-medium text-base-content/80">
                    Steps (
                    {props.recommendedTips.type === "percentage"
                      ? "%"
                      : auth.user.value?.restaurant.currency || "CHF"}
                    )
                  </span>
                  <input
                    type="text"
                    inputmode="decimal"
                    class="input input-bordered input-sm md:input-md"
                    placeholder={
                      props.recommendedTips.type === "percentage"
                        ? "e.g. 5, 10, 15"
                        : "e.g. 1.00, 2.00, 5.00"
                    }
                    value={stepsInput.value}
                    onInput={(e: Event) => {
                      stepsInput.value = (e.target as HTMLInputElement).value;
                    }}
                    onBlur={handleStepsBlur}
                  />
                  <span class="label-text-alt text-xs text-base-content/60">
                    Comma-separated values shown as quick-tip buttons.
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
});
